import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { fmtDate, ACTION_STATUS, statusLabel } from "@/lib/format";
import { WorkflowSteps } from "@/components/WorkflowSteps";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useTableSort, SortTh, ExportCsvButton } from "@/components/SortableHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { FolderSync, CalendarClock, Play, Eye, CheckCircle2, XCircle, RotateCw, RefreshCw, Ban, Trash2, Eraser, Terminal, Copy, BellRing, ArrowRight, Zap, Cloud, GitCompareArrows, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";

const JOB_ICON = { directorySync: FolderSync, groupSync: FolderSync, userSync: FolderSync, lifecycle: CalendarClock, offboard: CalendarClock, offboardDigest: BellRing, onPremReconcile: GitCompareArrows };

const RUN_STATUS = {
  success: ["text-emerald-600", "border-emerald-200 bg-emerald-100 text-emerald-800", CheckCircle2],
  warning: ["text-amber-600", "border-amber-200 bg-amber-100 text-amber-800", AlertTriangle],
  error: ["text-red-600", "border-red-200 bg-red-100 text-red-800", XCircle],
};

function RunRow({ r, t }) {
  const [open, setOpen] = useState(false);
  const [iconCls, badgeCls, Icon] = RUN_STATUS[r.status] || RUN_STATUS.success;
  const isReconcile = r.jobType === "onPremReconcile";
  const d = r.detail || {};
  const sm = d.summary || {};
  const rows = isReconcile ? [
    ["reconcile.usersMissingEntra", sm.usersMissingInEntra],
    ["reconcile.usersMissingAd", sm.usersMissingInAd],
    ["reconcile.enabledMismatch", sm.userEnabledMismatch],
    ["reconcile.groupsMissingEntra", sm.groupsMissingInEntra],
    ["reconcile.groupsMissingAd", sm.groupsMissingInAd],
    ["reconcile.membershipDivergence", sm.membershipDivergence],
    ["reconcile.errors", sm.errors],
  ].filter(([, v]) => v > 0) : [];
  const canExpand = isReconcile && (rows.length > 0 || d.counts);
  return (
    <div data-testid={`run-row-${r.id}`}>
      <div className="flex items-center justify-between px-5 py-3">
        <div className="flex items-center gap-3">
          <Icon size={16} className={iconCls} />
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-zinc-900">
              {isReconcile ? t("reconcile.title") : r.jobType} — {r.summary}
              {canExpand && (
                <button onClick={() => setOpen((o) => !o)} data-testid={`run-expand-${r.id}`} className="inline-flex items-center text-primary hover:underline">
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
              )}
            </div>
            <div className="font-mono text-[11px] text-zinc-400">{fmtDate(r.ranAt)}</div>
          </div>
        </div>
        <span className={`border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${badgeCls}`}>{statusLabel(r.status, t)}</span>
      </div>
      {open && canExpand && (
        <div className="border-t border-zinc-100 bg-zinc-50 px-5 py-3 text-xs" data-testid={`run-detail-${r.id}`}>
          {d.counts && (
            <div className="mb-2 font-mono text-[11px] text-zinc-500">
              AD users: {d.counts.adUsers} · Entra (on-prem) users: {d.counts.entraOnPremUsers} · AD groups: {d.counts.adGroups} · Entra (on-prem) groups: {d.counts.entraOnPremGroups}
            </div>
          )}
          {rows.length === 0 ? (
            <div className="text-emerald-600">{t("reconcile.clean")}</div>
          ) : (
            <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              {rows.map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border border-zinc-200 bg-white px-2 py-1">
                  <span className="text-zinc-600">{t(k)}</span>
                  <span className="font-mono font-bold text-amber-700">{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const DEBUG_LEVEL = {
  info: "text-sky-600 dark:text-sky-400",
  warn: "text-amber-600 dark:text-amber-400",
  error: "text-red-600 dark:text-red-400",
  debug: "text-zinc-400",
};

export default function Jobs() {
  const { isAdmin, can } = useAuth();
  const { t } = useLang();
  const [jobs, setJobs] = useState([]);
  const [runs, setRuns] = useState([]);
  const [queue, setQueue] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [pending, setPending] = useState([]);
  const [schedOnb, setSchedOnb] = useState([]);
  const [busy, setBusy] = useState("");
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [debug, setDebug] = useState(null); // { wid, entries, log } | null
  const [debugLoading, setDebugLoading] = useState("");
  const [obNow, setObNow] = useState(null); // scheduled row to fast-track | null
  const [obBusy, setObBusy] = useState(false);
  const [obCancel, setObCancel] = useState(null); // scheduled row to cancel | null
  const [cancelBusy, setCancelBusy] = useState(false);
  const [qStatus, setQStatus] = useState("all");
  const [qType, setQType] = useState("all");
  const sort = useTableSort("createdAt", "desc", "jobsQueue");

  const load = () => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
    api.get("/jobs/runs").then((r) => setRuns(r.data)).catch(() => {});
    api.get("/workflows?status=running,waiting,error,cancelled,finished&exclude_audit=true&limit=50").then((r) => setQueue(r.data)).catch(() => {});
    api.get("/workflows/scheduled").then((r) => setScheduled(r.data)).catch(() => {});
    api.get("/pending-onboards").then((r) => setPending(r.data)).catch(() => {});
    api.get("/scheduled-onboards").then((r) => setSchedOnb(r.data)).catch(() => {});
  };
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, []);

  const startPending = async (pid) => {
    setBusy(pid);
    try { await api.post(`/pending-onboards/${pid}/start`); toast.success(t("jobs.pendingStarted")); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const cancelPending = async (pid) => {
    setBusy(pid);
    try { await api.delete(`/pending-onboards/${pid}`); toast.success(t("jobs.pendingCancelled")); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const runSchedOnbNow = async (sid) => {
    setBusy(sid);
    try { await api.post(`/scheduled-onboards/${sid}/run-now`); toast.success(t("jobs.scheduledRan")); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const cancelSchedOnb = async (sid) => {
    setBusy(sid);
    try { await api.delete(`/scheduled-onboards/${sid}`); toast.success(t("jobs.scheduledCancelled")); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const run = async (key) => {
    setBusy(key);
    try { const { data } = await api.post(`/jobs/${key}/run`); toast.success(data.summary || `Ran ${key}`); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const retry = async (wid) => {
    setBusy(wid);
    try { await api.post(`/workflows/${wid}/retry`); toast.success(t("jobs.retried")); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const retryStep = async (wid, idx) => {
    setBusy(wid + idx);
    try { await api.post(`/workflows/${wid}/steps/${idx}/retry`); toast.success(t("jobs.retried")); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const retryCloudSync = async (wid) => {
    setBusy(wid);
    try { await api.post(`/workflows/${wid}/retry-cloud-sync`); toast.success(t("jobs.cloudSyncRetried")); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const viewDebug = async (wid) => {
    setDebugLoading(wid);
    try { const { data } = await api.get(`/workflows/${wid}/debug`); setDebug({ wid, ...data }); }
    catch (e) { toast.error(apiErr(e)); } finally { setDebugLoading(""); }
  };

  const copyDebug = async () => {
    try { await navigator.clipboard.writeText(debug?.log || ""); toast.success(t("jobs.copied")); }
    catch { toast.error("Copy failed"); }
  };

  const cancel = async (wid) => {
    setBusy(wid);
    try { await api.post(`/workflows/${wid}/cancel`); toast.success(t("jobs.cancelled")); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const del = async (wid) => {
    setBusy(wid);
    try { await api.delete(`/workflows/${wid}`); toast.success(t("jobs.deleted")); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const offboardNowConfirm = async () => {
    if (!obNow) return;
    setObBusy(true);
    try { await api.post(`/users/${obNow.userId}/offboard-now`); toast.success(t("jobs.offboardNowDone")); setObNow(null); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setObBusy(false); }
  };

  const cancelScheduledConfirm = async () => {
    if (!obCancel) return;
    setCancelBusy(true);
    try { await api.post(`/users/${obCancel.userId}/offboard-cancel`); toast.success(t("jobs.cancelOffboardDone")); setObCancel(null); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setCancelBusy(false); }
  };

  const clearCompleted = async () => {
    setClearing(true);
    try { const { data } = await api.post("/workflows/clear-completed"); toast.success(`${t("jobs.cleared")} (${data.deleted})`); setClearOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setClearing(false); }
  };

  const completedCount = queue.filter((w) => ["finished", "error", "cancelled"].includes(w.status)).length;

  const queueTypes = useMemo(() => Array.from(new Set(queue.map((w) => w.type))).sort(), [queue]);
  const queueStatuses = useMemo(() => Array.from(new Set(queue.map((w) => w.status))).sort(), [queue]);
  const shownQueue = useMemo(() => {
    const rows = queue.filter((w) => (qStatus === "all" || w.status === qStatus) && (qType === "all" || w.type === qType));
    return sort.sortRows(rows, { workflow: (w) => w.title });
  }, [queue, qStatus, qType, sort]);
  const queueCsvHeaders = [
    { key: "title", label: "Workflow" }, { key: "type", label: "Type" },
    { key: "when", label: "When" }, { key: "status", label: "Status" },
  ];
  const queueCsvRows = shownQueue.map((w) => ({ title: w.title, type: w.type, when: fmtDate(w.createdAt), status: w.status }));

  // Shared status + step-dialog + cancel/delete controls (used by the desktop table and mobile cards).
  const QueueStatus = ({ w }) => (
    <div className="flex items-center gap-2">
      <span className={`border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ACTION_STATUS[w.status] || ""}`}>{statusLabel(w.status, t)}</span>
      {["running", "waiting"].includes(w.status) && (w.steps || [])[w.currentStep]?.key === "verifyAccountState" && (
        <span data-testid={`queue-cloud-sync-${w.id}`} title="AD action done — waiting for Entra cloud sync to reflect the change" className="inline-flex items-center gap-1 border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">
          <Cloud size={10} className="animate-pulse" /> Cloud sync
        </span>
      )}
    </div>
  );

  const QueueActions = ({ w }) => (
    <div className="flex items-center gap-3 md:justify-end">
      <Dialog>
        <DialogTrigger asChild>
          <button data-testid={`queue-view-${w.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><Eye size={14} /> Steps</button>
        </DialogTrigger>
        <DialogContent className="rounded-sm sm:max-w-2xl" data-testid={`wf-dialog-${w.id}`}>
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <DialogTitle className="text-sm">{w.title} · {statusLabel(w.status, t)}</DialogTitle>
                <DialogDescription className="font-mono text-[11px]">{w.id}</DialogDescription>
              </div>
              {isAdmin && (
                <Button variant="outline" size="sm" onClick={() => viewDebug(w.id)} disabled={debugLoading === w.id} data-testid={`wf-debug-${w.id}`} className="mr-6 shrink-0 rounded-sm border-zinc-300 text-xs">
                  <Terminal size={13} className="mr-1.5" /> {t("jobs.viewDebug")}
                </Button>
              )}
            </div>
          </DialogHeader>
          <div className="space-y-2">
            <WorkflowSteps wf={w} isAdmin={isAdmin} onRetryStep={retryStep} busy={busy} />
          </div>
          {isAdmin && (w.status === "error" || ["running", "waiting"].includes(w.status)) && (
            <DialogFooter className="gap-2">
              {["running", "waiting"].includes(w.status) && (
                <Button variant="outline" onClick={() => cancel(w.id)} disabled={busy === w.id} data-testid={`wf-cancel-${w.id}`} className="rounded-sm border-red-200 text-red-600 hover:bg-red-50">
                  <Ban size={14} className="mr-1.5" /> {t("jobs.cancel")}
                </Button>
              )}
              {w.status === "error" && (
                <Button onClick={() => retry(w.id)} disabled={busy === w.id} data-testid={`wf-retry-${w.id}`} className="rounded-sm bg-primary hover:bg-[#002480]">
                  <RotateCw size={14} className="mr-1.5" /> {t("jobs.retryStep")}
                </Button>
              )}
              {w.status === "error" && (w.steps || []).some((st) => st.action === "provisionOnDemand" || st.key === "verifyUserExists") && (
                <Button variant="outline" onClick={() => retryCloudSync(w.id)} disabled={busy === w.id} data-testid={`wf-retry-cloudsync-${w.id}`} className="rounded-sm border-primary/40 text-primary hover:bg-primary/5">
                  <RefreshCw size={14} className="mr-1.5" /> {t("jobs.retryCloudSync")}
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      {isAdmin && ["running", "waiting"].includes(w.status) && (
        <button onClick={() => cancel(w.id)} disabled={busy === w.id} data-testid={`queue-cancel-${w.id}`} title={t("jobs.cancel")} className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline disabled:opacity-50"><Ban size={14} /> {t("jobs.cancel")}</button>
      )}
      {isAdmin && ["cancelled", "error", "finished"].includes(w.status) && (
        <button onClick={() => del(w.id)} disabled={busy === w.id} data-testid={`queue-delete-${w.id}`} title={t("jobs.delete")} className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-400 hover:text-red-600 disabled:opacity-50"><Trash2 size={14} /> {t("jobs.delete")}</button>
      )}
    </div>
  );


  return (
    <>
      <PageHeader title={t("jobs.title")} subtitle={t("jobs.subtitle")} />
      <div className="p-6 md:p-10">
        <Tabs defaultValue="queue">
          <TabsList className="rounded-sm bg-zinc-100">
            <TabsTrigger value="queue" data-testid="tab-queue">{t("jobs.queue")}</TabsTrigger>
            <TabsTrigger value="runs" data-testid="tab-runs">{t("jobs.runs")}</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="tab-jobs">{t("jobs.background")}</TabsTrigger>
          </TabsList>

          <TabsContent value="jobs" className="mt-6">
            <div className="grid grid-cols-1 gap-px bg-zinc-200 md:grid-cols-2">
              {jobs.map((j) => {
                const Icon = JOB_ICON[j.key] || FolderSync;
                return (
                  <div key={j.key} className="bg-white p-5" data-testid={`job-${j.key}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center bg-primary/10 text-primary"><Icon size={18} /></div>
                        <div>
                          <div className="text-sm font-bold text-zinc-900">{j.name}</div>
                          <div className="font-mono text-[11px] text-zinc-400">{j.schedule}</div>
                        </div>
                      </div>
                      <span className={`border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${j.enabled ? "border-emerald-200 bg-emerald-100 text-emerald-800" : "border-zinc-200 bg-zinc-100 text-zinc-500"}`}>
                        {j.enabled ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                    <div className="mt-4 flex items-center justify-between border-t border-zinc-100 pt-3">
                      <span className="font-mono text-[11px] text-zinc-400">{t("jobs.lastRun")}: {j.lastRun ? fmtDate(j.lastRun) : "—"}</span>
                      {isAdmin && (
                        <Button size="sm" variant="outline" onClick={() => run(j.key)} disabled={busy === j.key} data-testid={`run-${j.key}`} className="rounded-sm border-zinc-300">
                          <Play size={14} className="mr-1.5" /> {t("common.runNow")}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="queue" className="mt-6">
            {isAdmin && completedCount > 0 && (
              <div className="mb-3 flex justify-end">
                <Button variant="outline" size="sm" onClick={() => setClearOpen(true)} data-testid="clear-completed-btn" className="rounded-sm border-zinc-300 text-zinc-600 hover:border-red-300 hover:text-red-600">
                  <Eraser size={14} className="mr-1.5" /> {t("jobs.clearCompleted")} ({completedCount})
                </Button>
              </div>
            )}
            <div className="mb-3 flex items-center justify-end gap-2">
              <ExportCsvButton label={t("cols.export")} filename="job-queue.csv" headers={queueCsvHeaders} rows={queueCsvRows} />
            </div>
            <div className="hidden border border-zinc-200 bg-white md:block">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                    <SortTh label="Workflow" sortKey="workflow" sort={sort} /><SortTh label="Type" sortKey="type" sort={sort} className="hidden md:table-cell" filter={{ value: qType, onChange: setQType, allValue: "all", options: [{ value: "all", label: t("filter.allTypes") }, ...queueTypes.map((ty) => ({ value: ty, label: ty }))] }} />
                    <SortTh label="When" sortKey="createdAt" sort={sort} className="hidden lg:table-cell" /><SortTh label="Status" sortKey="status" sort={sort} filter={{ value: qStatus, onChange: setQStatus, allValue: "all", options: [{ value: "all", label: t("filter.allStatuses") }, ...queueStatuses.map((s) => ({ value: s, label: statusLabel(s, t) }))] }} /><th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {queue.length === 0 && scheduled.length === 0 && pending.length === 0 && schedOnb.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-zinc-400">Nothing in flight.</td></tr>}
                  {schedOnb.map((s) => (
                    <tr key={s.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`scheduled-onboard-row-${s.id}`}>
                      <td className="px-4 py-3 text-sm font-semibold text-zinc-900">{t("jobs.onboarding")} {s.displayName}</td>
                      <td className="hidden px-4 py-3 text-sm text-zinc-600 md:table-cell">onboard</td>
                      <td className="hidden px-4 py-3 text-sm text-zinc-500 lg:table-cell">{t("jobs.scheduledFor")}: {new Date(s.scheduledFor).toLocaleString()}{s.status === "error" ? ` — ${t("jobs.scheduleErr")}` : ""}</td>
                      <td className="px-4 py-3"><span className={`border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${s.status === "error" ? "border-red-300 bg-red-50 text-red-700" : "border-indigo-300 bg-indigo-50 text-indigo-700"}`}>{s.status === "error" ? t("jobs.scheduleErr") : t("jobs.scheduled")}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-3">
                          {can("onboarding") && (
                            <button onClick={() => runSchedOnbNow(s.id)} disabled={busy === s.id} data-testid={`sched-onboard-runnow-${s.id}`} title={t("jobs.runNow")} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:underline disabled:opacity-50">
                              <Play size={13} /> {t("jobs.runNow")}
                            </button>
                          )}
                          {can("onboarding") && (
                            <button onClick={() => cancelSchedOnb(s.id)} disabled={busy === s.id} data-testid={`sched-onboard-cancel-${s.id}`} title={t("jobs.cancelSchedule")} className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-500 hover:text-red-600 hover:underline disabled:opacity-50">
                              <Ban size={13} /> {t("jobs.cancelSchedule")}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {pending.map((p) => (
                    <tr key={p.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`pending-onboard-row-${p.id}`}>
                      <td className="px-4 py-3 text-sm font-semibold text-zinc-900">{t("jobs.onboarding")} {p.displayName}</td>
                      <td className="hidden px-4 py-3 text-sm text-zinc-600 md:table-cell">onboard</td>
                      <td className="hidden px-4 py-3 text-sm text-zinc-500 lg:table-cell">{t("jobs.waitingLicense")}: {(p.requiredLicenses || []).map((l) => l.name).join(", ")}</td>
                      <td className="px-4 py-3"><span className="border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">{t("jobs.waitingLicenseShort")}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-3">
                          {can("onboarding") && (
                            <button onClick={() => startPending(p.id)} disabled={busy === p.id} data-testid={`pending-start-${p.id}`} title={t("jobs.startNow")} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:underline disabled:opacity-50">
                              <Play size={13} /> {t("jobs.startNow")}
                            </button>
                          )}
                          {can("onboarding") && (
                            <button onClick={() => cancelPending(p.id)} disabled={busy === p.id} data-testid={`pending-cancel-${p.id}`} title={t("jobs.cancel")} className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-500 hover:text-red-600 hover:underline disabled:opacity-50">
                              <Ban size={13} /> {t("jobs.cancel")}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {scheduled.map((w) => (
                    <tr key={w.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`scheduled-row-${w.userId}`}>
                      <td className="px-4 py-3 text-sm font-semibold text-zinc-900">{w.title}</td>
                      <td className="hidden px-4 py-3 text-sm text-zinc-600 md:table-cell">{w.type}</td>
                      <td className="hidden px-4 py-3 text-sm text-zinc-500 lg:table-cell">{t("jobs.leavesOn")} {fmtDate(w.leaveDate)}</td>
                      <td className="px-4 py-3"><span className={`border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ACTION_STATUS.scheduled}`}>{t("jobs.scheduled")}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-3">
                          {can("offboarding") && (
                            <button onClick={() => setObNow(w)} disabled={obBusy} data-testid={`scheduled-offboard-now-${w.userId}`} title={t("jobs.offboardNow")} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 hover:underline disabled:opacity-50">
                              <Zap size={13} /> {t("jobs.offboardNow")}
                            </button>
                          )}
                          {can("offboarding") && (
                            <button onClick={() => setObCancel(w)} disabled={cancelBusy} data-testid={`scheduled-cancel-${w.userId}`} title={t("jobs.cancelOffboard")} className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-500 hover:text-red-600 hover:underline disabled:opacity-50">
                              <Ban size={13} /> {t("jobs.cancelOffboard")}
                            </button>
                          )}
                          <Link to={`/employees/${w.userId}`} data-testid={`scheduled-view-${w.userId}`} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                            {t("jobs.viewEmployee")} <ArrowRight size={13} />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {shownQueue.map((w) => (
                    <tr key={w.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`queue-row-${w.id}`}>
                      <td className="px-4 py-3 text-sm font-semibold text-zinc-900">{w.title}</td>
                      <td className="hidden px-4 py-3 text-sm text-zinc-600 md:table-cell">{w.type}</td>
                      <td className="hidden px-4 py-3 text-sm text-zinc-500 lg:table-cell">{fmtDate(w.createdAt)}</td>
                      <td className="px-4 py-3">
                        <QueueStatus w={w} />
                      </td>
                      <td className="px-4 py-3">
                        <QueueActions w={w} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list (pending + scheduled + queue) */}
            <div className="space-y-3 md:hidden" data-testid="queue-mobile-list">
              {queue.length === 0 && scheduled.length === 0 && pending.length === 0 && schedOnb.length === 0 && (
                <div className="border border-zinc-200 bg-white px-4 py-10 text-center text-sm text-zinc-400">Nothing in flight.</div>
              )}
              {schedOnb.map((s) => (
                <div key={s.id} className="border border-zinc-200 bg-white p-4" data-testid={`sched-onboard-card-${s.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-semibold text-zinc-900">{t("jobs.onboarding")} {s.displayName}</div>
                    <span className={`shrink-0 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${s.status === "error" ? "border-red-300 bg-red-50 text-red-700" : "border-indigo-300 bg-indigo-50 text-indigo-700"}`}>{s.status === "error" ? t("jobs.scheduleErr") : t("jobs.scheduled")}</span>
                  </div>
                  <div className="mt-1 text-xs text-zinc-500">{t("jobs.scheduledFor")}: {new Date(s.scheduledFor).toLocaleString()}</div>
                  {can("onboarding") && (
                    <div className="mt-3 flex items-center gap-4 border-t border-zinc-100 pt-3">
                      <button onClick={() => runSchedOnbNow(s.id)} disabled={busy === s.id} data-testid={`m-sched-onboard-runnow-${s.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:underline disabled:opacity-50"><Play size={13} /> {t("jobs.runNow")}</button>
                      <button onClick={() => cancelSchedOnb(s.id)} disabled={busy === s.id} data-testid={`m-sched-onboard-cancel-${s.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-500 hover:text-red-600 disabled:opacity-50"><Ban size={13} /> {t("jobs.cancelSchedule")}</button>
                    </div>
                  )}
                </div>
              ))}
              {pending.map((p) => (
                <div key={p.id} className="border border-zinc-200 bg-white p-4" data-testid={`pending-onboard-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-semibold text-zinc-900">{t("jobs.onboarding")} {p.displayName}</div>
                    <span className="shrink-0 border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">{t("jobs.waitingLicenseShort")}</span>
                  </div>
                  <div className="mt-1 text-xs text-zinc-500">{t("jobs.waitingLicense")}: {(p.requiredLicenses || []).map((l) => l.name).join(", ")}</div>
                  {can("onboarding") && (
                    <div className="mt-3 flex items-center gap-4 border-t border-zinc-100 pt-3">
                      <button onClick={() => startPending(p.id)} disabled={busy === p.id} data-testid={`m-pending-start-${p.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:underline disabled:opacity-50"><Play size={13} /> {t("jobs.startNow")}</button>
                      <button onClick={() => cancelPending(p.id)} disabled={busy === p.id} data-testid={`m-pending-cancel-${p.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-500 hover:text-red-600 disabled:opacity-50"><Ban size={13} /> {t("jobs.cancel")}</button>
                    </div>
                  )}
                </div>
              ))}
              {scheduled.map((w) => (
                <div key={w.id} className="border border-zinc-200 bg-white p-4" data-testid={`scheduled-card-${w.userId}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-semibold text-zinc-900">{w.title}</div>
                    <span className={`shrink-0 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ACTION_STATUS.scheduled}`}>{t("jobs.scheduled")}</span>
                  </div>
                  <div className="mt-1 text-xs text-zinc-500">{t("jobs.leavesOn")} {fmtDate(w.leaveDate)}</div>
                  <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-zinc-100 pt-3">
                    {can("offboarding") && <button onClick={() => setObNow(w)} disabled={obBusy} data-testid={`m-scheduled-offboard-now-${w.userId}`} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 hover:underline disabled:opacity-50"><Zap size={13} /> {t("jobs.offboardNow")}</button>}
                    {can("offboarding") && <button onClick={() => setObCancel(w)} disabled={cancelBusy} data-testid={`m-scheduled-cancel-${w.userId}`} className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-500 hover:text-red-600 disabled:opacity-50"><Ban size={13} /> {t("jobs.cancelOffboard")}</button>}
                    <Link to={`/employees/${w.userId}`} data-testid={`m-scheduled-view-${w.userId}`} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">{t("jobs.viewEmployee")} <ArrowRight size={13} /></Link>
                  </div>
                </div>
              ))}
              {shownQueue.map((w) => (
                <div key={w.id} className="border border-zinc-200 bg-white p-4" data-testid={`queue-card-${w.id}`}>
                  <div className="text-sm font-semibold text-zinc-900">{w.title}</div>
                  <div className="mt-0.5 font-mono text-[11px] text-zinc-400">{w.type} · {fmtDate(w.createdAt)}</div>
                  <div className="mt-2"><QueueStatus w={w} /></div>
                  <div className="mt-3 border-t border-zinc-100 pt-3"><QueueActions w={w} /></div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="runs" className="mt-6">
            <div className="border border-zinc-200 bg-white divide-y divide-zinc-100">
              {runs.length === 0 && <div className="px-5 py-10 text-center text-sm text-zinc-400">No job runs yet.</div>}
              {runs.map((r) => <RunRow key={r.id} r={r} t={t} />)}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!debug} onOpenChange={(o) => !o && setDebug(null)}>
        <DialogContent className="rounded-sm sm:max-w-3xl" data-testid="debug-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <DialogTitle className="text-sm">{t("jobs.debugTitle")}</DialogTitle>
                <DialogDescription className="font-mono text-[11px]">{debug?.wid}</DialogDescription>
              </div>
              <Button variant="outline" size="sm" onClick={copyDebug} data-testid="debug-copy-btn" className="mr-6 shrink-0 rounded-sm border-zinc-300 text-xs">
                <Copy size={13} className="mr-1.5" /> {t("jobs.copy")}
              </Button>
            </div>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto rounded-sm border border-zinc-200 bg-zinc-50" data-testid="debug-log">
            <table className="w-full border-collapse font-mono text-[11px]">
              <tbody>
                {(debug?.entries || []).map((e, i) => (
                  <tr key={i} className="border-b border-zinc-100 last:border-0 align-top" data-testid={`debug-entry-${i}`}>
                    <td className="whitespace-nowrap px-2 py-1 text-zinc-400">{e.ts ? fmtDate(e.ts) : "—"}</td>
                    <td className="whitespace-nowrap px-2 py-1 text-zinc-500">{e.system}</td>
                    <td className="px-2 py-1">
                      <span className={`inline-block w-12 text-center text-[9px] font-bold uppercase tracking-wider ${DEBUG_LEVEL[e.level] || "text-zinc-500"}`}>{e.level}</span>
                    </td>
                    <td className="px-2 py-1 text-zinc-700">{e.message}</td>
                  </tr>
                ))}
                {(debug?.entries || []).length === 0 && (
                  <tr><td className="px-3 py-6 text-center text-zinc-400">No log entries.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!obCancel} onOpenChange={(o) => !o && setObCancel(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="offboard-cancel-dialog">
          <DialogHeader><DialogTitle className="text-base">{t("jobs.cancelOffboardConfirm")}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500">{t("jobs.cancelOffboardDesc")}{obCancel ? ` — ${obCancel.title.replace(/^Offboard /, "")}` : ""}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setObCancel(null)} data-testid="offboard-cancel-back" className="rounded-sm border-zinc-300">{t("jobs.keepScheduled")}</Button>
            <Button onClick={cancelScheduledConfirm} disabled={cancelBusy} data-testid="offboard-cancel-confirm" className="rounded-sm bg-red-600 hover:bg-red-700">
              <Ban size={14} className="mr-1.5" /> {t("jobs.cancelOffboard")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={!!obNow} onOpenChange={(o) => !o && setObNow(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="offboard-now-dialog">
          <DialogHeader><DialogTitle className="text-base">{t("jobs.offboardNowConfirm")}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500">{t("jobs.offboardNowDesc")}{obNow ? ` — ${obNow.title.replace(/^Offboard /, "")}` : ""}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setObNow(null)} data-testid="offboard-now-cancel" className="rounded-sm border-zinc-300">{t("jobs.cancel")}</Button>
            <Button onClick={offboardNowConfirm} disabled={obBusy} data-testid="offboard-now-confirm" className="rounded-sm bg-amber-600 hover:bg-amber-700">
              <Zap size={14} className="mr-1.5" /> {t("jobs.offboardNow")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="clear-completed-dialog">
          <DialogHeader><DialogTitle className="text-base">{t("jobs.clearCompletedConfirm")}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500">{t("jobs.clearCompletedDesc")}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setClearOpen(false)} data-testid="clear-completed-cancel" className="rounded-sm border-zinc-300">{t("jobs.cancel")}</Button>
            <Button onClick={clearCompleted} disabled={clearing} data-testid="clear-completed-confirm" className="rounded-sm bg-red-600 hover:bg-red-700">
              <Eraser size={14} className="mr-1.5" /> {t("jobs.clearCompleted")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
